import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2 } from "lucide-react";
import { STAGES } from "@/lib/constants";

const RevertDialog = ({ client, open, setOpen, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const prev = STAGES[STAGES.indexOf(client.journey_stage) - 1];
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/revert-stage`, { reason });
      toast.success(`Moved back to ${prev}`);
      setOpen(false); setReason(""); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not move back"); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="revert-stage-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Move back to "{prev}"?</AlertDialogTitle>
          <AlertDialogDescription>From "{client.journey_stage}". The reason is saved in the audit log.</AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="revert-stage-reason" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="revert-stage-cancel">Cancel</AlertDialogCancel>
          <Button onClick={submit} disabled={!reason.trim() || busy} data-testid="revert-stage-confirm">Move back</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const DeleteDialog = ({ client, open, setOpen }) => {
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success(`${client.full_name} deleted permanently`);
      navigate("/app/clients");
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="delete-client-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {client.full_name} permanently?</AlertDialogTitle>
          <AlertDialogDescription>
            This erases the client and all their plans, policies, risk profiles, documents, notes, emails and appointments. It cannot be undone. Type DELETE to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" data-testid="delete-client-confirm-input" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-client-cancel">Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={submit} disabled={typed !== "DELETE" || busy} data-testid="delete-client-confirm">Delete permanently</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const AdminClientActions = ({ client, onChanged }) => {
  const [revertOpen, setRevertOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canRevert = STAGES.indexOf(client.journey_stage) > 0;
  return (
    <>
      {canRevert && (
        <Button variant="outline" onClick={() => setRevertOpen(true)} className="gap-2" data-testid="revert-stage-btn">
          <ArrowLeft className="h-4 w-4" /> Move back a stage
        </Button>
      )}
      <Button variant="outline" onClick={() => setDeleteOpen(true)} className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-client-btn">
        <Trash2 className="h-4 w-4" /> Delete client
      </Button>
      {canRevert && <RevertDialog client={client} open={revertOpen} setOpen={setRevertOpen} onDone={onChanged} />}
      <DeleteDialog client={client} open={deleteOpen} setOpen={setDeleteOpen} />
    </>
  );
};
